import React, { useState, useCallback, useRef } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  makeStyles
} from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { components } from 'cng-web-lib'

const {
  button: { CngButton }
} = components

const MAX_PREVIEW_BILLS = 3
const ACCEPTED_EXT = '.xlsx'
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
    width: 754,
    maxWidth: '100%'
  },
  titleRoot: {
    padding: theme.spacing(2.5, 3, 1.5)
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  titleText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 700,
    fontSize: 16,
    color: theme.palette.primary.main
  },
  closeBtn: {
    padding: 4,
    color: theme.palette.text.secondary
  },
  infoBox: {
    background: theme.palette.background.sectionOddBg || theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: theme.spacing(0.6, 0),
    '& + &': {
      borderTop: `1px solid ${theme.palette.divider}`
    }
  },
  infoLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontWeight: 500,
    flexShrink: 0,
    marginRight: theme.spacing(2)
  },
  infoValue: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    textAlign: 'right',
    wordBreak: 'break-all'
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 13,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary
  },
  dropZone: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: `1.5px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    '&:hover': { borderColor: theme.palette.primary.main }
  },
  dropZoneActive: {
    borderColor: `${theme.palette.primary.main} !important`,
    backgroundColor: theme.palette.primary.light + '14'
  },
  dropZoneError: {
    borderColor: `${theme.palette.error.main} !important`
  },
  fileLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: 1,
    minWidth: 0
  },
  fileIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 6,
    backgroundColor: theme.palette.primary.light + '22',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.main,
    fontSize: 16,
    flexShrink: 0
  },
  fileName: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  fileHint: { fontSize: 11, color: theme.palette.text.secondary },
  clearBtn: {
    color: theme.palette.text.disabled,
    padding: 4,
    '&:hover': { color: theme.palette.error.main }
  },
  infoBanner: {
    display: 'flex',
    gap: theme.spacing(1.5),
    background: theme.palette.info.light + '33',
    border: `1px solid ${theme.palette.info.light}`,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginTop: theme.spacing(1.5)
  },
  infoIcon: {
    color: theme.palette.info.dark,
    marginTop: 2,
    flexShrink: 0
  },
  warningBanner: {
    display: 'flex',
    gap: theme.spacing(1.5),
    background: theme.palette.warning.light + '33',
    border: `1px solid ${theme.palette.warning.light}`,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginTop: theme.spacing(1.5)
  },
  warningIcon: {
    color: theme.palette.warning.dark,
    marginTop: 2,
    flexShrink: 0
  },
  bannerText: {
    fontSize: 12,
    color: theme.palette.text.primary,
    lineHeight: 1.6
  },
  viewMoreLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    '&:hover': { textDecoration: 'underline' }
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 3)
  },
  requiredStar: {
    color: theme.palette.error.main,
    marginLeft: 2,
    fontWeight: 700
  },
  helpText: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 11,
    lineHeight: 1.5
  },
  helpIcon: {
    color: theme.palette.info.main,
    fontSize: 12,
    marginTop: 1,
    flexShrink: 0
  }
}))

/**
 * EditDraftsDialog — upload a revised Excel file to update draft bills.
 *
 * Props:
 *  open       - boolean
 *  row        - upload history row object
 *  onClose    - fn()
 *  onConfirm  - fn(file: File)
 *  submitting - boolean
 */
function EditDraftsDialog({ open, row, onClose, onConfirm, submitting }) {
  const classes = useStyles()
  const fileInputRef = useRef(null)

  const [file, setFile] = useState(null)
  const [fileError, setFileError] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [showAllBills, setShowAllBills] = useState(false)

  function validateFile(f) {
    if (!f) return 'Please select a file.'
    const ext = '.' + f.name.split('.').pop().toLowerCase()
    if (ext !== ACCEPTED_EXT) return 'Invalid format. Only .xlsx files are accepted.'
    if (f.size > MAX_FILE_SIZE) return 'File exceeds 10 MB limit.'
    return ''
  }

  const handleFileSelect = useCallback((e) => {
    const f = e.target.files[0]
    if (!f) return
    const err = validateFile(f)
    setFileError(err)
    setFile(err ? null : f)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files[0]
    if (!f) return
    const err = validateFile(f)
    setFileError(err)
    setFile(err ? null : f)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (!row) return null

  const allBills = Array.isArray(row.manifestHeaderIds) ? row.manifestHeaderIds : []
  const billCount = allBills.length
  const previewBills = showAllBills ? allBills : allBills.slice(0, MAX_PREVIEW_BILLS)
  const hasMore = !showAllBills && billCount > MAX_PREVIEW_BILLS
  const billsDisplay = previewBills.join(', ') + (hasMore ? ' ...' : '')

  function handleClose() {
    setFile(null)
    setFileError('')
    setShowAllBills(false)
    onClose()
  }

  function handleConfirm() {
    const err = validateFile(file)
    if (err) { setFileError(err); return }
    onConfirm(file)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.dialogPaper }}
      disableBackdropClick={submitting}
      disableEscapeKeyDown={submitting}
    >
      {/* Header */}
      <DialogTitle disableTypography classes={{ root: classes.titleRoot }}>
        <Box className={classes.titleRow}>
          <Typography className={classes.titleText}>
            <FontAwesomeIcon icon={['fal', 'pen-to-square']} />
            Edit / Update Draft(s)
          </Typography>
          <IconButton size='small' className={classes.closeBtn} onClick={handleClose} disabled={submitting}>
            <FontAwesomeIcon icon={['fal', 'xmark']} />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent style={{ padding: '20px 24px 12px' }}>
        {/* Detail rows */}
        <Box className={classes.infoBox}>
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>File Upload ID:</Typography>
            <Typography className={classes.infoValue}>{row.id || '—'}</Typography>
          </Box>
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>File Name:</Typography>
            <Typography className={classes.infoValue}>{row.fileName || '—'}</Typography>
          </Box>
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>Draft Bill(s):</Typography>
            <Typography className={classes.infoValue} style={{ maxWidth: 340 }}>
              {billCount === 0 ? '—' : (
                <>
                  {billsDisplay}
                  {hasMore && (
                    <>
                      {' '}
                      <span className={classes.viewMoreLink} onClick={() => setShowAllBills(true)}>
                        View More
                      </span>
                    </>
                  )}
                </>
              )}
            </Typography>
          </Box>
        </Box>

        {/* File upload section */}
        <Typography className={classes.sectionTitle}>
          Upload Updated Excel File
          <span className={classes.requiredStar}>*</span>
        </Typography>

        <input
          ref={fileInputRef}
          type='file'
          accept='.xlsx'
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={submitting}
        />
        <Box
          className={[
            classes.dropZone,
            dragOver ? classes.dropZoneActive : '',
            fileError ? classes.dropZoneError : ''
          ].filter(Boolean).join(' ')}
          onDragOver={(e) => { e.preventDefault(); if (!submitting) setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={!submitting ? handleDrop : undefined}
        >
          <Box
            className={classes.fileLabel}
            onClick={() => !submitting && fileInputRef.current && fileInputRef.current.click()}
          >
            <Box className={classes.fileIconWrap}>
              <FontAwesomeIcon icon={['fal', file ? 'file-excel' : 'cloud-arrow-up']} />
            </Box>
            <Box style={{ minWidth: 0 }}>
              <Typography className={classes.fileName}>
                {file ? file.name : 'Choose a file or drag & drop here'}
              </Typography>
              <Typography className={classes.fileHint}>
                {file
                  ? `${(file.size / 1024).toFixed(1)} KB · Click to change`
                  : '.xlsx · Max 10 MB'}
              </Typography>
            </Box>
          </Box>
          {file && !submitting && (
            <IconButton
              size='small'
              className={classes.clearBtn}
              onClick={() => { setFile(null); setFileError('') }}
            >
              <FontAwesomeIcon icon={['fal', 'xmark']} />
            </IconButton>
          )}
        </Box>

        {fileError && (
          <Typography variant='caption' color='error' style={{ marginTop: 6, display: 'block' }}>
            <FontAwesomeIcon icon={['fal', 'circle-exclamation']} style={{ marginRight: 4 }} />
            {fileError}
          </Typography>
        )}

        {/* Help text - associated with file upload section */}
        <Box className={classes.helpText}>
          <FontAwesomeIcon icon={['fal', 'circle-info']} className={classes.helpIcon} />
          <Typography style={{ fontSize: 11, lineHeight: 1.5, color: 'inherit' }}>
            Upload a revised file to update the draft record(s) created from this upload.
            {' '}Use the &ldquo;Template for Updating Bills&rdquo; to retain the System IDs.
          </Typography>
        </Box>

        {/* Warning banner */}
        <Box className={classes.infoBanner}>
          <FontAwesomeIcon icon={['fal', 'circle-info']} className={classes.infoIcon} />
          <Typography className={classes.bannerText}>
            The draft record(s) will be revised with the contents of the uploaded file.
            <br />
            They remain as drafts until you submit them to NAIC.
          </Typography>
        </Box>
      </DialogContent>

      {/* Footer */}
      <Box className={classes.footer}>
        <CngButton color='secondary' size='medium' disabled={submitting} onClick={handleClose}>
          Cancel
        </CngButton>
        <CngButton
          color='primary'
          size='medium'
          disabled={submitting || !file}
          onClick={handleConfirm}
          startIcon={
            submitting
              ? <FontAwesomeIcon icon={['fal', 'spinner']} spin />
              : <FontAwesomeIcon icon={['fal', 'upload']} />
          }
        >
          {submitting ? 'Uploading...' : 'Upload & Edit Draft(s)'}
        </CngButton>
      </Box>
    </Dialog>
  )
}

export default EditDraftsDialog